<script setup lang="ts">
/**
 * 统一 RGB 拾色悬浮面板：色盘 + 色相条 + RGB 输入 + 确定/取消。
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import {
  hexToHsv,
  hsvToHex,
  hsvToRgb,
  rgbToHsv,
} from '@/utils/colorPickerMath'

const PANEL_WIDTH = 236
const PANEL_HEIGHT = 252

const props = withDefaults(defineProps<{
  show: boolean
  modelValue: string
  anchorLeft?: number
  anchorTop?: number
  anchorHeight?: number
}>(), {
  anchorLeft: 0,
  anchorTop: 0,
  anchorHeight: 28,
})

const emit = defineEmits<{
  'update:show': [value: boolean]
  'update:modelValue': [value: string]
  confirm: [value: string]
  cancel: []
}>()

const hue = ref(0)
const saturation = ref(1)
const valueLevel = ref(1)
const channelR = ref('255')
const channelG = ref('255')
const channelB = ref('255')
const svDragging = ref(false)
const hueDragging = ref(false)

const currentHex = computed(() => hsvToHex(hue.value, saturation.value, valueLevel.value))
const svBaseColor = computed(() => {
  const [r, g, b] = hsvToRgb(hue.value, 1, 1)
  return `rgb(${r}, ${g}, ${b})`
})
const svCursorStyle = computed(() => ({
  left: `${Math.round(saturation.value * 100)}%`,
  top: `${Math.round((1 - valueLevel.value) * 100)}%`,
}))
const hueCursorStyle = computed(() => ({
  left: `${Math.round((hue.value / 360) * 100)}%`,
}))
const panelStyle = computed(() => {
  const margin = 8
  let left = props.anchorLeft
  let top = props.anchorTop + props.anchorHeight + 6
  const maxLeft = Math.max(margin, window.innerWidth - PANEL_WIDTH - margin)
  const maxTop = Math.max(margin, window.innerHeight - PANEL_HEIGHT - margin)
  left = Math.max(margin, Math.min(left, maxLeft))
  top = Math.max(margin, Math.min(top, maxTop))
  return {
    left: `${left}px`,
    top: `${top}px`,
    width: `${PANEL_WIDTH}px`,
  }
})

/** 从外部 hex 同步 HSV 与 RGB 输入框。 */
function syncFromHex(hex: string): void {
  const [h, s, v] = hexToHsv(hex)
  hue.value = h
  saturation.value = s
  valueLevel.value = v
  syncRgbInputsFromHsv()
}

function syncRgbInputsFromHsv(): void {
  const [r, g, b] = hsvToRgb(hue.value, saturation.value, valueLevel.value)
  channelR.value = String(r)
  channelG.value = String(g)
  channelB.value = String(b)
}

function emitLiveValue(): void {
  emit('update:modelValue', currentHex.value)
}

function applyHsv(nextHue: number, nextSaturation: number, nextValue: number): void {
  hue.value = nextHue
  saturation.value = clampUnitLocal(nextSaturation)
  valueLevel.value = clampUnitLocal(nextValue)
  syncRgbInputsFromHsv()
  emitLiveValue()
}

function clampUnitLocal(value: number): number {
  return Math.max(0, Math.min(1, value))
}

function readChannel(text: string, fallback: number): number {
  const parsed = Number(text)
  return Number.isFinite(parsed) ? Math.max(0, Math.min(255, Math.round(parsed))) : fallback
}

function applyRgbInput(): void {
  const r = readChannel(channelR.value, 0)
  const g = readChannel(channelG.value, 0)
  const b = readChannel(channelB.value, 0)
  channelR.value = String(r)
  channelG.value = String(g)
  channelB.value = String(b)
  const [h, s, v] = rgbToHsv(r, g, b)
  hue.value = h
  saturation.value = s
  valueLevel.value = v
  emitLiveValue()
}

function updateSvFromPointer(clientX: number, clientY: number, rect: DOMRect): void {
  const nextSaturation = clampUnitLocal((clientX - rect.left) / rect.width)
  const nextValue = clampUnitLocal(1 - (clientY - rect.top) / rect.height)
  applyHsv(hue.value, nextSaturation, nextValue)
}

function updateHueFromPointer(clientX: number, rect: DOMRect): void {
  const ratio = clampUnitLocal((clientX - rect.left) / rect.width)
  applyHsv(ratio * 360, saturation.value, valueLevel.value)
}

function onSvPointerDown(event: PointerEvent): void {
  const target = event.currentTarget as HTMLElement
  target.setPointerCapture(event.pointerId)
  svDragging.value = true
  updateSvFromPointer(event.clientX, event.clientY, target.getBoundingClientRect())
}

function onSvPointerMove(event: PointerEvent): void {
  if (!svDragging.value) return
  const target = event.currentTarget as HTMLElement
  updateSvFromPointer(event.clientX, event.clientY, target.getBoundingClientRect())
}

function onSvPointerUp(event: PointerEvent): void {
  svDragging.value = false
  const target = event.currentTarget as HTMLElement
  if (target.hasPointerCapture(event.pointerId)) target.releasePointerCapture(event.pointerId)
}

function onHuePointerDown(event: PointerEvent): void {
  const target = event.currentTarget as HTMLElement
  target.setPointerCapture(event.pointerId)
  hueDragging.value = true
  updateHueFromPointer(event.clientX, target.getBoundingClientRect())
}

function onHuePointerMove(event: PointerEvent): void {
  if (!hueDragging.value) return
  const target = event.currentTarget as HTMLElement
  updateHueFromPointer(event.clientX, target.getBoundingClientRect())
}

function onHuePointerUp(event: PointerEvent): void {
  hueDragging.value = false
  const target = event.currentTarget as HTMLElement
  if (target.hasPointerCapture(event.pointerId)) target.releasePointerCapture(event.pointerId)
}

function closePanel(): void {
  emit('update:show', false)
}

function handleConfirm(): void {
  const hex = currentHex.value
  closePanel()
  emit('confirm', hex)
}

function handleCancel(): void {
  closePanel()
  emit('cancel')
}

function handleOutsidePointerDown(event: PointerEvent): void {
  if (!props.show) return
  const target = event.target
  if (!(target instanceof Element)) return
  if (target.closest('.app-color-picker-panel__card')) return
  if (target.closest('.app-color-picker__trigger')) return
  handleCancel()
}

watch(
  () => props.show,
  (show) => {
    if (!show) return
    syncFromHex(props.modelValue)
  },
)

watch(
  () => props.modelValue,
  (hex) => {
    if (props.show || svDragging.value || hueDragging.value) return
    syncFromHex(hex)
  },
)

watch(
  () => props.show,
  (show) => {
    if (show) {
      document.addEventListener('pointerdown', handleOutsidePointerDown, true)
      return
    }
    document.removeEventListener('pointerdown', handleOutsidePointerDown, true)
  },
)

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleOutsidePointerDown, true)
})
</script>

<template>
  <Teleport to="body">
    <div v-if="show" class="app-color-picker-panel">
      <div
        class="app-color-picker-panel__card"
        :style="panelStyle"
        @pointerdown.stop
      >
        <div
          class="app-color-picker-panel__sv"
          :style="{ backgroundColor: svBaseColor }"
          @pointerdown="onSvPointerDown"
          @pointermove="onSvPointerMove"
          @pointerup="onSvPointerUp"
          @pointercancel="onSvPointerUp"
        >
          <span class="app-color-picker-panel__sv-cursor" :style="svCursorStyle" />
        </div>
        <div
          class="app-color-picker-panel__hue"
          @pointerdown="onHuePointerDown"
          @pointermove="onHuePointerMove"
          @pointerup="onHuePointerUp"
          @pointercancel="onHuePointerUp"
        >
          <span class="app-color-picker-panel__hue-cursor" :style="hueCursorStyle" />
        </div>
        <div class="app-color-picker-panel__rgb">
          <span class="app-color-picker-panel__rgb-label">RGB</span>
          <input
            class="app-color-picker-panel__rgb-input"
            type="text"
            inputmode="numeric"
            :value="channelR"
            @change="channelR = ($event.target as HTMLInputElement).value; applyRgbInput()"
          />
          <input
            class="app-color-picker-panel__rgb-input"
            type="text"
            inputmode="numeric"
            :value="channelG"
            @change="channelG = ($event.target as HTMLInputElement).value; applyRgbInput()"
          />
          <input
            class="app-color-picker-panel__rgb-input"
            type="text"
            inputmode="numeric"
            :value="channelB"
            @change="channelB = ($event.target as HTMLInputElement).value; applyRgbInput()"
          />
        </div>
        <div class="app-color-picker-panel__actions">
          <button type="button" class="app-color-picker-panel__btn" @click.stop="handleCancel">取消</button>
          <button type="button" class="app-color-picker-panel__btn app-color-picker-panel__btn--primary" @click.stop="handleConfirm">确定</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.app-color-picker-panel {
  position: fixed;
  inset: 0;
  z-index: 4100;
  pointer-events: none;
}

.app-color-picker-panel__card {
  position: fixed;
  z-index: 1;
  display: grid;
  gap: 8px;
  padding: 8px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
  box-sizing: border-box;
  pointer-events: auto;
  box-shadow: 0 0 0 1px var(--theme-border);
}

.app-color-picker-panel__sv {
  position: relative;
  height: 120px;
  border: 1px solid var(--theme-border);
  touch-action: none;
  cursor: crosshair;
}

.app-color-picker-panel__sv::before,
.app-color-picker-panel__sv::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.app-color-picker-panel__sv::before {
  background: linear-gradient(to right, #ffffff, transparent);
}

.app-color-picker-panel__sv::after {
  background: linear-gradient(to top, #000000, transparent);
}

.app-color-picker-panel__sv-cursor {
  position: absolute;
  width: 12px;
  height: 12px;
  margin: -6px 0 0 -6px;
  border: 2px solid #ffffff;
  box-sizing: border-box;
  pointer-events: none;
}

.app-color-picker-panel__hue {
  position: relative;
  height: 14px;
  border: 1px solid var(--theme-border);
  background: linear-gradient(
    to right,
    #ff0000 0%,
    #ffff00 17%,
    #00ff00 33%,
    #00ffff 50%,
    #0000ff 67%,
    #ff00ff 83%,
    #ff0000 100%
  );
  touch-action: none;
  cursor: pointer;
}

.app-color-picker-panel__hue-cursor {
  position: absolute;
  top: 0;
  width: 8px;
  height: 100%;
  margin-left: -4px;
  border: 2px solid #ffffff;
  box-sizing: border-box;
  pointer-events: none;
}

.app-color-picker-panel__rgb {
  display: grid;
  grid-template-columns: 34px repeat(3, minmax(0, 1fr));
  gap: 6px;
  align-items: center;
}

.app-color-picker-panel__rgb-label {
  color: var(--theme-text-secondary);
  font-size: 12px;
}

.app-color-picker-panel__rgb-input {
  width: 100%;
  min-width: 0;
  height: 28px;
  padding: 0 6px;
  border: 1px solid var(--theme-border);
  color: var(--theme-text-heading);
  background: var(--theme-bg-card);
  font-size: 12px;
  box-sizing: border-box;
}

.app-color-picker-panel__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.app-color-picker-panel__btn {
  height: 30px;
  padding: 0;
  border: 1px solid var(--theme-border);
  color: var(--theme-text-heading);
  background: var(--theme-bg-card);
  font-size: 13px;
  cursor: pointer;
}

.app-color-picker-panel__btn--primary {
  border-color: var(--theme-accent);
  color: var(--theme-text-inverse);
  background: var(--theme-accent);
}
</style>
